import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, ChevronDown } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import PlacementPromptSheet from '../components/PlacementPromptSheet';
import { plantsApi } from '../services/gardenApi';
import { GARDEN_SUN_EXPOSURE_OPTIONS } from '../constants';
import { useGardenPageTitle } from '../useGardenPageTitle';

const SUN_NEEDS = GARDEN_SUN_EXPOSURE_OPTIONS;
const WATER_NEEDS = ['low', 'medium', 'high'];

// Step 2 of the Scan Plant Tag flow. Shows exactly the 6 fields from the
// spec, pre-filled with whatever the AI extracted. User can edit anything,
// confirm to save, or switch to the full manual entry form.
//
// REQUIRED: never auto-save — the user must explicitly confirm.
export default function ScanConfirmScreen() {
  useGardenPageTitle('Confirm plant', 'Confirm plant');
  const navigate = useNavigate();
  const location = useLocation();
  const scan = location.state?.scan || null;

  // If someone deep-links to /scan/confirm without state, just bounce them to
  // the scan step so they can start over.
  useEffect(() => {
    if (!scan) navigate('/garden/plants/scan', { replace: true });
  }, [scan, navigate]);

  const prefill = scan?.plant || {};
  const [form, setForm] = useState({
    common_name:    prefill.common_name    || '',
    botanical_name: prefill.botanical_name || '',
    variety:        prefill.variety        || '',
    sun_needs:      prefill.sun_needs      || '',
    water_needs:    prefill.water_needs    || '',
    notes:          buildNotesDefault(prefill),
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showOcr, setShowOcr] = useState(false);
  const [savedPlant, setSavedPlant] = useState(null);
  const [placementOpen, setPlacementOpen] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const canSave = useMemo(() => !!form.common_name.trim(), [form.common_name]);

  const handleConfirm = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Notes from the scan go into foliage_notes (plain-text catch-all that
      // already renders on the plant detail screen). We also persist the
      // scan provenance so the user can audit it later.
      const body = {
        common_name:    form.common_name.trim(),
        botanical_name: trimOrNull(form.botanical_name),
        variety:        trimOrNull(form.variety),
        sun_needs:      form.sun_needs || null,
        water_needs:    form.water_needs || null,
        foliage_notes:  trimOrNull(form.notes),
        // Carry through the AI-suggested bloom_color if the user didn't edit
        // it out of the notes — this is a small quality-of-life win since
        // bloom_color is one of the spec's target fields.
        bloom_color:    trimOrNull(prefill.bloom_color),
        source_type:    'tag_scan',
        source_image:   scan?.image_url || null,
        raw_ocr_text:   scan?.ocr_text  || null,
      };
      const r = await plantsApi.create(body);
      setSavedPlant(r.plant);
      setPlacementOpen(true);
    } catch (e) {
      setError(e.message || 'Could not save this plant. Try again.');
      setSaving(false);
    }
  };

  const switchToManual = () => {
    // Hand the current (possibly edited) values to the full manual form so
    // nothing is lost. PlantFormScreen doesn't read location.state today, so
    // for MVP we simply navigate — user keeps what they typed on this screen
    // mentally. (Full prefill would require widening PlantFormScreen.)
    navigate('/garden/plants/new');
  };

  if (!scan) return null;

  return (
    <div className="pb-32">
      <header className="flex items-center gap-2 px-2 pt-3 pb-2 sticky top-0 bg-garden-surface z-10">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">
          Confirm plant
        </h1>
      </header>

      <div className="px-4 space-y-3">
        {scan.image_url || scan.ocr_text ? (
          <div className="rounded-2xl bg-white border border-black/5 p-3 flex gap-3 items-start">
            {scan.image_url ? (
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-garden-primaryLight shrink-0">
                <img
                  src={scan.image_url}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
            ) : null}
            <div className="flex-1 min-w-0 text-sm text-garden-muted leading-snug">
              <div className="inline-flex items-center gap-1.5 text-garden-primaryDark font-medium">
                <Sparkles size={14} />
                {scan.ai_failed
                  ? 'We couldn\u2019t read the tag automatically'
                  : scan.ai_available === false
                    ? 'AI is not configured'
                    : 'We filled in what we could find'}
              </div>
              <div className="mt-1">
                Check every field before saving. You can edit anything.
              </div>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
        ) : null}

        <div className="rounded-2xl bg-white border border-black/5 p-4 space-y-3">
          <Field label="Plant name" required>
            <input
              value={form.common_name}
              onChange={(e) => update({ common_name: e.target.value })}
              placeholder="e.g. Hydrangea"
              className="scan-input"
              autoFocus
            />
          </Field>
          <Field label="Botanical name">
            <input
              value={form.botanical_name}
              onChange={(e) => update({ botanical_name: e.target.value })}
              placeholder="e.g. Hydrangea macrophylla"
              className="scan-input italic"
            />
          </Field>
          <Field label="Variety">
            <input
              value={form.variety}
              onChange={(e) => update({ variety: e.target.value })}
              placeholder="e.g. Endless Summer"
              className="scan-input"
            />
          </Field>
          <Field label="Sun">
            <select
              value={form.sun_needs}
              onChange={(e) => update({ sun_needs: e.target.value })}
              className="scan-input capitalize"
            >
              <option value="">—</option>
              {SUN_NEEDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Water">
            <select
              value={form.water_needs}
              onChange={(e) => update({ water_needs: e.target.value })}
              className="scan-input capitalize"
            >
              <option value="">—</option>
              {WATER_NEEDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              rows={3}
              placeholder="Anything else from the tag"
              className="scan-input"
            />
          </Field>
        </div>

        {scan.ocr_text ? (
          <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowOcr((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 min-h-11"
            >
              <span className="text-sm font-medium text-garden-ink">
                Show raw text from tag
              </span>
              <ChevronDown
                size={18}
                className={`text-garden-muted transition-transform ${showOcr ? 'rotate-180' : ''}`}
              />
            </button>
            {showOcr ? (
              <pre className="px-4 pb-4 text-xs text-garden-muted whitespace-pre-wrap border-t border-black/5 pt-3 max-h-60 overflow-auto">
                {scan.ocr_text}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 bg-white/95 backdrop-blur border-t border-black/5 p-3">
        <div className="mx-auto max-w-md space-y-2">
          <PrimaryButton
            onClick={handleConfirm}
            disabled={!canSave || saving}
            className="w-full"
          >
            {saving ? 'Saving\u2026' : 'Confirm & save'}
          </PrimaryButton>
          <button
            type="button"
            onClick={switchToManual}
            disabled={saving}
            className="w-full min-h-10 text-sm text-garden-muted underline underline-offset-2 disabled:opacity-50"
          >
            Switch to manual entry
          </button>
        </div>
      </div>

      <PlacementPromptSheet
        open={placementOpen}
        plant={savedPlant}
        onClose={() => {
          setPlacementOpen(false);
          if (savedPlant) navigate(`/garden/plants/${savedPlant.id}`, { replace: true });
        }}
      />

      <style>{`
        .scan-input {
          width: 100%;
          min-height: 48px;
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid rgba(31, 42, 24, 0.15);
          background: #fff;
          color: #1f2a18;
          font-size: 16px;
          outline: none;
        }
        .scan-input:focus { border-color: #3B6D11; }
        textarea.scan-input { min-height: 88px; resize: vertical; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-garden-muted">
        {label}{required ? ' *' : ''}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function trimOrNull(s) {
  if (typeof s !== 'string') return null;
  const t = s.trim();
  return t === '' ? null : t;
}

// If the AI gave us a bloom_color but no notes, surface it in the notes box
// so it's visible and editable (we also still persist bloom_color separately).
function buildNotesDefault(prefill) {
  const parts = [];
  if (prefill.bloom_color) parts.push(`Bloom color: ${prefill.bloom_color}`);
  if (prefill.notes)       parts.push(prefill.notes);
  return parts.join('\n').trim();
}
